import { DateTime } from 'luxon';
import { AllocationForm, AllocationPartForm, PhaseForm } from '../Timeline.types';
import { PhaseGroup } from './Allocation.state';

export function calculateAllocationForm(
    phaseGroups: PhaseGroup[],
    timelineStartDateTime: DateTime,
    timelineEndDateTime: DateTime,
): AllocationForm {
    if (phaseGroups.length === 0) {
        throw new Error('Unexpected phase group length');
    }

    const firstPhaseGroup = phaseGroups[0];
    const lastPhaseGroup = phaseGroups.at(-1)!; // We make sure there is at least 1 element in the array with the check above
    const lastPhaseGroupEndDateTime = lastPhaseGroup.startDateTime.plus({ days: lastPhaseGroup.confirmedCellSpan });

    const allocationStartsWithin = firstPhaseGroup.startDateTime >= timelineStartDateTime;
    const allocationEndsWithin = lastPhaseGroupEndDateTime <= timelineEndDateTime;

    // Allocation is entirely contained in the timeline range
    if (allocationStartsWithin && allocationEndsWithin) {
        return 'Start-End';
    }

    // First phase starts within timeline + ends after
    if (allocationStartsWithin && !allocationEndsWithin) {
        return 'Start-Middle';
    }

    // First phase starts before timeline + ends within
    if (!allocationStartsWithin && allocationEndsWithin) {
        return 'Middle-End';
    }

    return 'Middle';
}

export function calculateConfirmedPhaseForm(
    timelineStartDateTime: DateTime,
    timelineEndDateTime: DateTime,
    allocationStartDateTime: DateTime,
    allocationEndDateTime: DateTime,
    phaseStartDateTime: DateTime,
    phaseEndDateTime: DateTime,
    hasTemporaryPhasesBefore: boolean,
    hasTemporaryPhasesAfter: boolean,
    ignoreTimelineRange: boolean,
): PhaseForm {
    const isLeftEnd = getIsLeftEnd(
        timelineStartDateTime,
        allocationStartDateTime,
        phaseStartDateTime,
        hasTemporaryPhasesBefore,
        ignoreTimelineRange,
    );
    const isRightEnd = getIsRightEnd(
        timelineEndDateTime,
        allocationEndDateTime,
        phaseEndDateTime,
        hasTemporaryPhasesAfter,
        ignoreTimelineRange,
    );

    if (isLeftEnd && isRightEnd) {
        return 'Start-End';
    }

    if (isLeftEnd && !isRightEnd) {
        return 'Start-Middle';
    }

    if (!isLeftEnd && isRightEnd) {
        return 'Middle-End';
    }

    return 'Middle';
}

function getIsLeftEnd(
    timelineStartDateTime: DateTime,
    allocationStartDateTime: DateTime,
    phaseStartDateTime: DateTime,
    hasTemporaryPhasesBefore: boolean,
    ignoreTimelineRange: boolean,
) {
    // There are temporary phases before, so this phase cannot be the start
    if (hasTemporaryPhasesBefore) {
        return false;
    }

    // Use Case: Open edit popup displays the entire allocation, no matter the timeline range
    if (ignoreTimelineRange) {
        return true;
    }

    // Is this the first phase of the allocation?
    if (phaseStartDateTime.equals(allocationStartDateTime)) {
        // Is the start of the allocation within the timeline range (i.e. not before)?
        return allocationStartDateTime >= timelineStartDateTime;
    }

    return false;
}

function getIsRightEnd(
    timelineEndDateTime: DateTime,
    allocationEndDateTime: DateTime,
    phaseEndDateTime: DateTime,
    hasTemporaryPhasesAfter: boolean,
    ignoreTimelineRange: boolean,
) {
    // There are temporary phases before, so this phase cannot be the end
    if (hasTemporaryPhasesAfter) {
        return false;
    }

    // Use Case: Open edit popup displays the entire allocation, no matter the timeline range
    if (ignoreTimelineRange) {
        return true;
    }

    // Is this the last phase of the allocation?
    if (phaseEndDateTime.equals(allocationEndDateTime)) {
        // Is the end of the allocation within the timeline range (i.e. not after)?
        return allocationEndDateTime <= timelineEndDateTime;
    }

    return false;
}

// A phase can span multiple days, but we need to represent each day/column separately for rendering in the timeline
export function mapPhaseFormsToAllocationParts(
    columnCount: number,
    phaseForm: PhaseForm,
    hasTemporaryPhasesBefore: boolean,
    hasTemporaryPhasesAfter: boolean,
): AllocationPartForm[] {
    return Array.from({ length: columnCount }).map((_, index) => {
        if (phaseForm === 'Start-End') {
            if (columnCount === 1 && !hasTemporaryPhasesBefore && !hasTemporaryPhasesAfter) {
                return 'Single' as const;
            }
            if (index === 0 && !hasTemporaryPhasesBefore) {
                return 'Start' as const;
            }
            if (index === columnCount - 1 && !hasTemporaryPhasesAfter) {
                return 'End' as const;
            }
        }

        if (phaseForm === 'Start-Middle' && !hasTemporaryPhasesBefore) {
            if (index === 0) {
                return 'Start' as const;
            }
        }

        if (phaseForm === 'Middle-End') {
            if (index === columnCount - 1 && !hasTemporaryPhasesAfter) {
                return 'End' as const;
            }
        }

        return 'Middle' as const;
    });
}

export function calculateInsertedPhases(
    cellSpan: number,
    isLeftEndPosition: boolean,
    isRightEndPosition: boolean,
): { form: AllocationPartForm }[] {
    if (cellSpan > 0) {
        return [...Array(cellSpan)].map((_, index) => {
            if (index === 0 && isLeftEndPosition) {
                return {
                    form: 'Start' as const,
                };
            }

            if (index === cellSpan - 1 && isRightEndPosition) {
                return {
                    form: 'End' as const,
                };
            }

            return {
                form: 'Middle' as const,
            };
        });
    }

    return [];
}
